// Reminders list (admin, header bell): every pending return visit sorted by date, due
// ones flagged; "New reminder" flips to an inline picker over the viewed week's calls
// and hands off to the same Return sheet each card's bell uses.
import { useSignal } from '@preact/signals';
import { fmtReminderWhen, todayISO } from '../../lib/dates';
import { returnFor, showReminders } from '../../state/adminUi';
import { clearReminder, reminders } from '../../state/reminders';
import { setParked } from '../../state/admin';
import { week } from '../../state/schedule';
import { toast } from '../../state/toast';
import { AdminSheet } from './AdminSheet';

export function RemindersSheet() {
  const close = () => (showReminders.value = false);
  const picking = useSignal(false);
  const pickQ = useSignal('');
  const today = todayISO();

  const items = Object.entries(reminders.value)
    .map(([id, r]) => ({ id, ...r }))
    .sort((a, b) => (a.date || '').localeCompare(b.date || ''));

  async function done(id: string) {
    const ok = await clearReminder(id);
    await setParked(id, false); // clearing the reminder un-grays the service (v1)
    if (ok) toast('Reminder cleared.');
  }

  function openReturn(id: string, development: string, address: string) {
    returnFor.value = { id, development, address };
  }

  const pickRows = (week.value?.days ?? [])
    .flatMap((d) =>
      d.calls.map((c) => ({
        id: c.id,
        development: c.development,
        address: c.address,
        name: c.name,
        day: (d.label || d.key).split(',')[0]!,
        slot: c.slot,
      })),
    )
    .filter((r) => {
      const q = pickQ.value.trim().toLowerCase();
      return !q || `${r.development} ${r.address} ${r.name} ${r.day}`.toLowerCase().includes(q);
    });

  return (
    <AdminSheet
      title="Reminders"
      sub={picking.value ? 'Pick a service from this week.' : 'Return visits to schedule.'}
      onClose={close}
      tall
      testId="reminders-sheet"
    >
      {!picking.value && (
        <>
          <button class="btn-ghost mb-2 w-full" onClick={() => (picking.value = true)}>
            ＋ New reminder
          </button>
          {items.length === 0 && (
            <p class="p-6 text-center text-slate-500">
              No reminders yet. Tap the bell on a service to schedule one.
            </p>
          )}
          <div class="space-y-2">
            {items.map((it) => (
              <div
                key={it.id}
                class="flex items-stretch gap-2 rounded-xl border border-slate-200 bg-white p-2"
              >
                <div class="min-w-0 flex-1 p-1">
                  <div class="flex flex-wrap items-center gap-1.5">
                    <span
                      class={`db-badge ${it.date && it.date <= today ? 'bg-red-100 text-missed-ink' : 'bg-slate-200 text-slate-700'}`}
                    >
                      {fmtReminderWhen(it.date)}
                    </span>
                    <span class="truncate font-bold text-navy-900">
                      {it.development || it.address || 'Service'}
                    </span>
                  </div>
                  {it.development && it.address && (
                    <p class="mt-0.5 truncate text-sm text-slate-600">{it.address}</p>
                  )}
                  {it.note && <p class="mt-0.5 text-sm text-slate-500">{it.note}</p>}
                </div>
                <div class="flex shrink-0 flex-col justify-center gap-1.5">
                  <button
                    class="mini-btn"
                    onClick={() => openReturn(it.id, it.development, it.address)}
                  >
                    Edit
                  </button>
                  <button class="mini-btn text-done-ink" onClick={() => void done(it.id)}>
                    Done
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {picking.value && (
        <>
          <div class="mb-2 flex gap-2">
            <input
              type="search"
              class="tap w-full rounded-xl border border-slate-300 px-3 py-2"
              placeholder="Search this week…"
              aria-label="Search services"
              value={pickQ.value}
              onInput={(e) => (pickQ.value = (e.target as HTMLInputElement).value)}
            />
            <button class="btn-ghost shrink-0" onClick={() => (picking.value = false)}>
              Back
            </button>
          </div>
          {pickRows.length === 0 && (
            <p class="p-6 text-center text-slate-500">
              {pickQ.value ? 'No services match your search.' : 'No services in this week yet.'}
            </p>
          )}
          <div class="space-y-2">
            {pickRows.map((r) => (
              <button
                key={r.id}
                type="button"
                class="tap w-full rounded-xl border border-slate-200 bg-white p-3 text-left"
                onClick={() => {
                  picking.value = false;
                  openReturn(r.id, r.development, r.address);
                }}
              >
                <div class="flex flex-wrap items-center gap-1.5">
                  <span class="db-badge bg-slate-200 text-slate-700">
                    {r.day}
                    {r.slot ? ` · ${r.slot}` : ''}
                  </span>
                  <span class="truncate font-bold text-navy-900">
                    {r.development || r.address || 'Service'}
                  </span>
                </div>
                {[r.development && r.address ? r.address : '', r.name].filter(Boolean).length >
                  0 && (
                  <p class="mt-0.5 truncate text-sm text-slate-600">
                    {[r.development && r.address ? r.address : '', r.name]
                      .filter(Boolean)
                      .join(' · ')}
                  </p>
                )}
              </button>
            ))}
          </div>
        </>
      )}
    </AdminSheet>
  );
}
